import type { CabinGeometry, SeatLayout, SeatMapData } from '@trippath/shared';

/** A cabin class: first, business (J or C), premium economy, economy. */
export type CabinClass = 'F' | 'J' | 'C' | 'W' | 'Y';

/** A row of seats: its number, class, seats across it by group, and where it is from the nose. */
export interface SeatRow {
  number: number;
  cabinClass: CabinClass;
  groups: string[][];
  /** Metres from the nose to the row's front. */
  at: number;
  /** Metres to the next row. */
  pitch: number;
  exit: boolean;
}

/** An aircraft laid out to scale, in metres: its size, its rows, windows, doors and exits. */
export interface SeatPlan {
  /** The airline's layout it is drawn from. */
  layout: SeatLayout;
  length: number;
  span: number;
  width: number;
  /** The frame pitch, in metres: a window between each two frames. */
  frame: number;
  wing: [number, number, number, number];
  rows: SeatRow[];
  windows: number[];
  doors: number[];
  exits: number[];
}

/** Where a seat is across its row: by a window, in the middle or on an aisle, on which side. */
export interface SeatPlace {
  row: number;
  letter: string;
  kind: 'window' | 'middle' | 'aisle';
  /** Which side of the aircraft; `centre` for the one seat right in its middle. */
  side: 'left' | 'right' | 'centre';
}

/** Seats across a row as said, `ABC-DEF`, by group. */
export function groupsOf(pattern: string): string[][] {
  return pattern
    .split('-')
    .filter((group) => group !== '')
    .map((group) => Array.from(group));
}

/** A layout read, `C12 Y168`: each class and its seats; none for anything else. */
export function classesOf(layout: string): [CabinClass, number][] {
  const known: Record<string, CabinClass> = { F: 'F', J: 'J', C: 'C', W: 'W', Y: 'Y' };
  const found: [CabinClass, number][] = [];
  for (const part of layout.trim().toUpperCase().split(/\s+/)) {
    const cabinClass = known[part.charAt(0)];
    const seats = part.slice(1);
    if (!cabinClass || !/^\d{1,3}$/.test(seats)) return [];
    found.push([cabinClass, Number(seats)]);
  }
  return found;
}

/** Row numbers as said, `13, 17`. */
function rowsOf(said: string): number[] {
  return said
    .split(/[,\s]+/)
    .filter((n) => n !== '')
    .map(Number);
}

/** The seats across a row of a class, as the type has them. */
function across(cabin: CabinGeometry, cabinClass: CabinClass): string[][] {
  const pattern =
    cabinClass === 'Y'
      ? cabin.economy
      : cabinClass === 'W'
        ? (cabin.premium ?? cabin.economy)
        : (cabin.business ?? cabin.economy);
  return groupsOf(pattern);
}

/** How far apart rows of a class are, in metres, when the layout does not say. */
function pitchOf(cabinClass: CabinClass, wide: boolean, economy: number): number {
  if (cabinClass === 'Y') return economy;
  if (cabinClass === 'W') return 0.97;
  return wide ? 1.9 : 0.9;
}

/**
 * A seat map laid out to scale: the type's length and span (its dimensions, else the cabin's
 * rows said how long), its rows by class from the layout, numbered from 1 past the rows it does
 * not number; its exit rows as the layout says, else those just behind the over-wing exits; a
 * window at each frame but by the doors. `null` without the type's cabin or a layout.
 */
export function planOf(map: SeatMapData): SeatPlan | null {
  const { cabin, layout } = map;
  if (!cabin || !layout) return null;
  const classes = classesOf(layout.classes);
  if (classes.length === 0) return null;
  const wide = groupsOf(cabin.economy).length > 2;
  const economy = (layout.pitchCm ?? 76) / 100;
  const blocks = classes.map(([cabinClass, seats]) => {
    const groups = across(cabin, cabinClass);
    const perRow = groups.reduce((sum, group) => sum + group.length, 0);
    return {
      cabinClass,
      groups,
      rows: Math.ceil(seats / perRow),
      pitch: pitchOf(cabinClass, wide, economy),
    };
  });
  const needed = blocks.reduce((sum, block) => sum + block.rows * block.pitch, 0);
  const share = cabin.cabin[1] - cabin.cabin[0];
  const length = map.dimensions?.lengthM ?? needed / share;
  const room = share * length;
  // Rows that would not fit are drawn closer: the layout is the airline's, the room the type's.
  const squeeze = Math.min(1, room / needed);
  const skip = new Set(rowsOf(layout.skipRows));
  const exitRows = new Set(rowsOf(layout.exitRows));
  const exits = cabin.exits.map((at) => at * length);
  const rows: SeatRow[] = [];
  let at = cabin.cabin[0] * length;
  let number = 1;
  for (const block of blocks) {
    for (let i = 0; i < block.rows; i += 1) {
      while (skip.has(number)) number += 1;
      const pitch = block.pitch * squeeze;
      rows.push({
        number,
        cabinClass: block.cabinClass,
        groups: block.groups,
        at,
        pitch,
        exit: exitRows.has(number),
      });
      at += pitch;
      number += 1;
    }
  }
  if (exitRows.size === 0) {
    for (const exit of exits) {
      const behind = rows.find((row) => row.at >= exit - 0.2);
      if (behind) behind.exit = true;
    }
  }
  const doors = cabin.doors.map((d) => d * length);
  const windows: number[] = [];
  for (
    let w = cabin.cabin[0] * length - 0.5;
    w <= cabin.cabin[1] * length + 0.3;
    w += cabin.frameM
  ) {
    if (!doors.some((d) => Math.abs(d - w) < 0.8)) windows.push(w);
  }
  return {
    layout,
    length,
    span: map.dimensions?.wingspanM ?? length * 0.95,
    width: cabin.widthM,
    frame: cabin.frameM,
    wing: cabin.wing,
    rows,
    windows,
    doors,
    exits,
  };
}

/** A seat as written, `24F`: its row and letter; `null` for anything else. */
export function seatOf(written: string): { row: number; letter: string } | null {
  const plain = written.replace(/\s/g, '').toUpperCase();
  return /^\d{1,3}[A-Z]$/.test(plain)
    ? { row: Number(plain.slice(0, -1)), letter: plain.slice(-1) }
    : null;
}

/**
 * Where a seat is across its row, from the seats across it: the first and last by a window, those
 * beside a gap by an aisle, the rest in the middle; on the left the groups before the middle and
 * the first half of a middle group. `null` when the row or the letter is not on the plan.
 */
export function placeOf(plan: SeatPlan, written: string): SeatPlace | null {
  const seat = seatOf(written);
  const row = seat && plan.rows.find((r) => r.number === seat.row);
  if (!seat || !row) return null;
  const at = row.groups.findIndex((group) => group.includes(seat.letter));
  const group = row.groups[at];
  if (!group) return null;
  const inGroup = group.indexOf(seat.letter);
  const lastGroup = row.groups.length - 1;
  const kind =
    (at === 0 && inGroup === 0) || (at === lastGroup && inGroup === group.length - 1)
      ? 'window'
      : inGroup === 0 || inGroup === group.length - 1
        ? 'aisle'
        : 'middle';
  // Across the middle group of an odd row its middle seat is the aircraft's own middle: 3-3-3's E.
  const middle = lastGroup / 2;
  const across = at === middle ? inGroup - (group.length - 1) / 2 : at - middle;
  const side = across < 0 ? 'left' : across > 0 ? 'right' : 'centre';
  return { row: seat.row, letter: seat.letter, kind, side };
}

/** Whether a window is beside a row: one whose middle is beside a sitter's head, the seat's back half. */
export function windowBeside(plan: SeatPlan, row: number): boolean {
  const found = plan.rows.find((r) => r.number === row);
  return (
    found !== undefined &&
    plan.windows.some((w) => w >= found.at + 0.05 && w <= found.at + 0.45)
  );
}

/** The most places across a row, its aisles counted: what a seat's width is drawn by. */
export function widest(plan: SeatPlan): number {
  return Math.max(
    1,
    ...plan.rows.map(
      (row) => row.groups.reduce((n, g) => n + g.length, 0) + row.groups.length - 1,
    ),
  );
}

/**
 * Where a row is along the aircraft: ahead of the wing, over it or behind it, and whether a door
 * is near, within 2.5 m of it; `null` for a row not on the plan.
 */
export function rowWhere(
  plan: SeatPlan,
  row: number,
): { wing: 'ahead' | 'over' | 'behind'; door: boolean } | null {
  const found = plan.rows.find((r) => r.number === row);
  if (!found) return null;
  const [front, back] = plan.wing;
  const at = found.at / plan.length;
  return {
    wing: at < front ? 'ahead' : at > back ? 'behind' : 'over',
    door: plan.doors.some((door) => Math.abs(door - found.at) <= 2.5),
  };
}
